import AttachablePanel from '@/lib/printess/attachablePanel'

export const SwinkelsFamilyBreweryMixin = (Base) =>
  class extends Base {
    #swinkelsFamilyBreweryEditor
    #swinkelsFamilyBreweryEditorPanel

    onEditorLoaded() {
      super.onEditorLoaded()

      this.#connectSwinkelsFamilyBreweryEditor()
    }

    // called from the bridge
    editSwinkelsFamilyBrewery() {
      this.#swinkelsFamilyBreweryEditorPanel.show()
    }

    // called from stimulus (button click)
    hideEditSwinkelsFamilyBreweryEditor() {
      this.#swinkelsFamilyBreweryEditorPanel.hide()
    }

    // called from base
    disconnectSwinkelsFamilyBrewery() {
      const editorDataInput = document.getElementById("product_personalization_editor_data")
      if (editorDataInput && this._onEditorDataChangeUpdateSwinkelsFamilyBrewery) {
        editorDataInput.removeEventListener('change', this._onEditorDataChangeUpdateSwinkelsFamilyBrewery)
      }

      if (!this.#swinkelsFamilyBreweryEditorPanel) return
      this.#swinkelsFamilyBreweryEditorPanel.detach()
    }

    // called from base
    updateSwinkelsFamilyBrewery() {
      if (!this.editorReady || !this.#swinkelsFamilyBreweryEditor) { return; }

      const editorDataInput = document.getElementById("product_personalization_editor_data")
      let data
      try {
        data = JSON.parse(editorDataInput.value)
      } catch (e) {
        return
      }

      const records = data.records || []

      const sfbBeers = records.filter(b => b.delivered_by && b.delivered_by !== "")
      const mappedSfbBeers = sfbBeers.map(this.#mapSfbBeer)

      const otherBeers = records.filter(b => !b.delivered_by || b.delivered_by === "")
      const mappedOtherBeers = otherBeers.map(this.#mapSfbOtherBeer)

      const lang = (data.language || 'nl').toLowerCase();
      const languageMap = {
        nl: 'dutch',
        fr: 'french',
        de: 'german',
        en: 'english'
      };

      const datasetPrefix = languageMap[lang] || languageMap['nl'];
      const bitternessKey = `${datasetPrefix}Bitterness`;
      const flavourKey = `${datasetPrefix}Flavour`;

      const bitterness = editorDataInput.dataset[bitternessKey] || '';
      const flavour = editorDataInput.dataset[flavourKey] || '';

      Promise.all([
        this.editor.api.setFormFieldValue('sfbBeer', JSON.stringify(mappedSfbBeers)),
        this.editor.api.setFormFieldValue('otherBeer', JSON.stringify(mappedOtherBeers)),
        this.editor.api.setFormFieldValue('language', lang.toUpperCase()),
        this.editor.api.setFormFieldValue('bitterness', bitterness),
        this.editor.api.setFormFieldValue('flavour', flavour)
      ]).then(() => {
        this.editor.api.clearSelection();
      });
    }

    #connectSwinkelsFamilyBreweryEditor() {
      this._onEditorDataChangeUpdateSwinkelsFamilyBrewery = this.updateSwinkelsFamilyBrewery.bind(this)

      const editorDataInput = document.getElementById("product_personalization_editor_data")
      if (editorDataInput) {
        editorDataInput.addEventListener('change', this._onEditorDataChangeUpdateSwinkelsFamilyBrewery)
      }

      this.#initSwinkelsFamilyBreweryEditor();
    }

    #initSwinkelsFamilyBreweryEditor() {
      this.#swinkelsFamilyBreweryEditor = document.getElementById('swinkels_family_brewery_editor');

      if (!this.#swinkelsFamilyBreweryEditor) return

      this.#swinkelsFamilyBreweryEditorPanel = new AttachablePanel(this.editor, this.#swinkelsFamilyBreweryEditor);
      this.#swinkelsFamilyBreweryEditorPanel.attach()
    }

    #mapSfbBeer(beer) {
      return {
        packshot: beer[`${beer.variant}_visual_url`],
        alcohol: beer.alcohol_by_volume,
        bitterness: String(beer.bitterness),

        price: beer.price1,
        volume: beer.volume1,
        unit: beer.unit1,
        price_2: beer.price2,
        volume_2: beer.volume2,
        unit_2: beer.unit2,
        price_3: beer.price3,
        volume_3: beer.volume3,
        unit_3: beer.unit3,

        hero: beer.hero,

        NL_productname: beer.name,
        NL_description: beer.dutch_flavour_description_160_characters,
        NL_hero_description: beer.dutch_frame_description_400_characters,
        NL_category: beer.dutch_flavour_category,
        NL_variant: beer.dutch_variant,
        NL_taste: beer.dutch_flavour_description_28_characters,
        NL_beerstyles: beer.dutch_style,

        EN_productname: beer.name,
        EN_description: beer.english_flavour_description_160_characters,
        EN_hero_description: beer.english_frame_description_400_characters,
        EN_category: beer.english_flavour_category,
        EN_variant: beer.english_variant,
        EN_taste: beer.english_flavour_description_28_characters,
        EN_beerstyles: beer.english_style,

        DE_productname: beer.name,
        DE_description: beer.german_flavour_description_160_characters,
        DE_hero_description: beer.german_frame_description_400_characters,
        DE_category: beer.german_flavour_category,
        DE_variant: beer.german_variant,
        DE_taste: beer.german_flavour_description_28_characters,
        DE_beerstyles: beer.german_style,

        FR_productname: beer.name,
        FR_description: beer.french_flavour_description_160_characters,
        FR_hero_description: beer.french_frame_description_400_characters,
        FR_category: beer.french_flavour_category,
        FR_variant: beer.french_variant,
        FR_taste: beer.french_flavour_description_28_characters,
        FR_beerstyles: beer.french_style,
      }
    }

    #mapSfbOtherBeer(beer) {
      return {
        productname: beer.name,
        alcohol: beer.alcohol_by_volume,

        price: beer.price1,
        volume: beer.volume1,
        unit: beer.unit1,
        price_2: beer.price2,
        volume_2: beer.volume2,
        unit_2: beer.unit2,
        price_3: beer.price3,
        volume_3: beer.volume3,
        unit_3: beer.unit3,
      }
    }
  };
